/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './IFrame';

const IFrameManager: ComponentManagerFn<'IFrame'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'IFrame'>(platform, 'IFrame'),
    type: 'IFrame',
    name: 'IFrame',
    isChildAllowed: (page, item, childType) =>
      (['Popover', 'PulseTip'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      src: {
        label: 'Src',
        type: 'expression',
        returnType: async () => 'string',
        category: 'General',
        helpText:
          'Specify an expression that should return the url for the Iframe.',
      },
      allow: {
        label: 'Allow',
        type: 'expression',
        returnType: async () => 'string',
        category: 'General',
        helpText:
          'Use this to selectively enable, disable, and modify the behavior of certain features of iframe. Eg: microphone, camera, geolocation.',
      },
      title: {
        label: 'Title',
        type: 'string',
        category: 'General',
        helpText: 'Please specify title for the IFrame',
      },
      layoutHeight: {
        label: 'Height',
        type: 'number',
        min: 200,
        max: 800,
        step: 40,
        category: 'General',
        helpText: 'Provide a number to set height of the Iframe.',
      },
      layoutWidth: {
        label: 'Width',
        type: 'number',
        min: 400,
        max: 2000,
        step: 40,
        category: 'General',
        helpText: 'Specify a number to set the width of the IFrame',
      },
      frameBorder: {
        label: 'Frame Border',
        type: 'boolean',
        category: 'General',
        helpText: 'Enable this to add border to the IFrame',
      },
      scrollBar: {
        label: 'Scroll Bar',
        type: 'boolean',
        category: 'General',
        helpText: 'Enable this to add a scrollbar to the IFrame',
      },
      margin: {
        label: 'Margin',
        type: 'margin',
        category: 'Spacing',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
    },
    getDisplayText: (item) => item.props.title || 'Iframe',
    category: 'Forms',
    icon: { iconName: 'desktop-alt' },
    description: `**Overview**<br/>
The IFrame (Inline Frame) component allows you to embed an independent HTML document or external website directly within your application page.
It creates a "window" to another source, effectively isolating the embedded content from the rest of your page. Use this to display external dashboards (like PowerBI or Tableau), embed YouTube videos, show Google Maps, or integrate legacy web applications without rewriting them.

**1. Source & Content**<br/>
To render content, you must point the frame to a valid URL.
* **Src:** An expression that resolves to the URL you want to display. (Example: \`'http://maps.google.com/...'\`).
* **Note:** Ensure the website you are embedding allows itself to be framed (i.e., it does not have \`X-Frame-Options: DENY\` headers).
* **Title:** A descriptive name for the frame (e.g., "Customer Map"). This is used for accessibility (screen readers) and does not visually appear on the page.

**2. Permissions (The "Allow" Prop)**<br/>
Modern browsers restrict what embedded content can do by default. You must explicitly grant permissions for sensitive features.
* **Allow:** A string specifying the feature policy.
* **Common Values:** \`camera; microphone; geolocation\`
* **Use Case:** If you are embedding a video conferencing tool or a map that requires the user's location, you must add the corresponding permission tokens here.

**3. Layout & Appearance**<br/>
You can control how the frame fits into your page layout.
* **Height / Width:** Numeric values to set the fixed dimensions of the frame (e.g., Height: 600, Width: 800).
* **Frame Border:** Toggles a visible border around the frame to separate it from the surrounding content.
* **Scroll Bar:** Allows the user to scroll inside the frame if the embedded content is larger than the frame's dimensions.
* **Margin / Padding:** Standard spacing controls to position the frame relative to other components.

**4. Composition**<br/>
The IFrame is primarily a display component, but it supports specific overlays.
* **Allowed Children:**
    * **Popover:** Display a popup when interacting with the frame container.
    * **PulseTip:** Add a pulsing help tip to draw attention to the frame.`,
  };
};

export default IFrameManager;
